import { useState } from 'react';
import { Bell, CheckCircle, Ticket, MessageSquare, UserPlus, Clock, RefreshCw, Trash2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { deleteNotification } from '../api/notifications';
import { toast } from 'sonner';

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ticket_update':
      case 'ticket_resolved':
        return <Ticket className="w-5 h-5" />;
      case 'message':
        return <MessageSquare className="w-5 h-5" />;
      case 'assignment':
        return <UserPlus className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'ticket_resolved':
        return 'text-green-500 bg-green-500/10 dark:bg-green-500/20';
      case 'ticket_update':
        return 'text-blue-500 bg-blue-500/10 dark:bg-blue-500/20';
      case 'message':
        return 'text-purple-500 bg-purple-500/10 dark:bg-purple-500/20';
      default:
        return 'text-gray-400 bg-gray-400/10 dark:bg-gray-500/20';
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteNotification(id);
      if (response.success) {
        toast.success('Notification deleted');
        fetchNotifications();
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  // Filter notifications client-side (since API might return all)
  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    return true;
  });

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gray-50/50 dark:bg-slate-950 pb-20">
        
        {/* ================= PREMIUM HEADER SECTION ================= */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 shadow-lg shadow-indigo-900/10">
          <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-5">
            
            {/* Top Row: Title, Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              
              {/* Left: Brand */}
              <div className="flex items-center gap-3 min-w-max">
                <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 text-white relative">
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white leading-tight">Notifications</h1>
                  <p className="text-blue-200 text-xs hidden sm:block">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                  </p>
                </div>
              </div>

              {/* Right: Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchNotifications}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-semibold border border-white/20 transition-all hover:scale-105"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-900/20 transition-all hover:scale-105"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">Mark All Read</span>
                  </button>
                )}
              </div>
            </div>

            {/* Bottom Row: Filter Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setFilter('all')}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                  filter === 'all'
                    ? 'bg-white text-indigo-700 border-white shadow-md'
                    : 'bg-white/10 text-blue-100 border-transparent hover:bg-white/20'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                  filter === 'unread'
                    ? 'bg-white text-indigo-700 border-white shadow-md'
                    : 'bg-white/10 text-blue-100 border-transparent hover:bg-white/20'
                }`}
              >
                Unread ({filteredNotifications.filter(n => !n.is_read).length})
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                  filter === 'read'
                    ? 'bg-white text-indigo-700 border-white shadow-md'
                    : 'bg-white/10 text-blue-100 border-transparent hover:bg-white/20'
                }`}
              >
                Read ({filteredNotifications.filter(n => n.is_read).length})
              </button>
            </div>
          </div>
        </div>

        {/* ================= CONTENT AREA ================= */}
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 mt-8">
          
          {/* Notifications List */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-800 overflow-hidden">
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-600 dark:text-gray-400 mt-4 text-sm font-medium">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Bell className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">No notifications</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-slate-800">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`group relative p-5 transition-all duration-300 hover:shadow-md ${
                      !notification.is_read
                        ? 'bg-blue-50/50 dark:bg-blue-900/10 border-l-4 border-l-blue-500'
                        : 'bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    {/* Hover accent bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-blue-600 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-bottom"></div>
                    
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`p-3 rounded-xl flex-shrink-0 shadow-sm ${getNotificationColor(notification.type)}`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <h3 className={`text-base font-bold ${
                                !notification.is_read
                                  ? 'text-gray-900 dark:text-white'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                {notification.title}
                              </h3>
                              {!notification.is_read && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                                <Clock className="w-3.5 h-3.5" />
                                <span>{formatTime(notification.created_at)}</span>
                              </div>
                              {notification.link && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(notification.link);
                                  }}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold transition-colors"
                                >
                                  View Details →
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-start gap-2 flex-shrink-0">
                            <div className="flex flex-col gap-1">
                              {!notification.is_read && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}
                                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                  title="Mark as read"
                                >
                                  <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(notification.id);
                                }}
                                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Notifications;

